/**
 * O que faz: Seção de configuração e visualização da Análise Semanal de Chats.
 * Onde usa: AISettingsForm (aba IA das configurações).
 * Conexões: useChatAnalysisConfig, useChatAnalysisReports, usePromptSuggestions,
 *           useTriggerManualAnalysis, useSaveChatAnalysisConfig, useAnalysisQuota.
 */

import { useState } from 'react';
import { BrainCircuit, ChevronDown, ChevronUp, Lock, Loader2, Play, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ThinkingOrbLoader } from '@/components/ui/ThinkingOrbLoader';
import {
  useChatAnalysisConfig,
  useChatAnalysisReports,
  usePromptSuggestions,
  useSaveChatAnalysisConfig,
  useTriggerManualAnalysis,
  useAnalysisQuota,
} from '@/hooks/useChatAnalysis';
import { PromptSuggestionCard } from './PromptSuggestionCard';
import { PERIODO_LABELS, SENTIMENTO_COLORS } from '@/types/chatAnalysis';

interface ChatAnalysisSectionProps {
  clinicaId: string;
  readOnly?: boolean;
  canUse?: boolean;
  analysisQuotaPerMonth?: number; // -1 = ilimitado, 0 = sem acesso
}

export const ChatAnalysisSection = ({
  clinicaId,
  readOnly = false,
  canUse = false,
  analysisQuotaPerMonth = 0,
}: ChatAnalysisSectionProps) => {
  const { data: config, isLoading: configLoading } = useChatAnalysisConfig(clinicaId);
  const { data: reports, isLoading: reportsLoading } = useChatAnalysisReports(clinicaId);
  const { data: suggestions } = usePromptSuggestions(clinicaId);
  const { data: quota } = useAnalysisQuota(clinicaId);
  const saveConfig = useSaveChatAnalysisConfig(clinicaId);
  const triggerAnalysis = useTriggerManualAnalysis(clinicaId);

  const [ativo, setAtivo] = useState<boolean | undefined>(undefined);
  const [expandedReport, setExpandedReport] = useState<string | null>(null);

  const analiseAtivo = ativo ?? config?.analise_chat_ativo ?? false;
  const hasDraft = ativo !== undefined;

  const runsThisMonth = quota?.runsThisMonth ?? 0;
  const isUnlimited = analysisQuotaPerMonth === -1;
  const quotaExceeded = !isUnlimited && runsThisMonth >= analysisQuotaPerMonth;
  const quotaPercent = isUnlimited ? 0 : Math.min((runsThisMonth / Math.max(analysisQuotaPerMonth, 1)) * 100, 100);

  const handleSave = () => {
    if (ativo === undefined) return;
    saveConfig.mutate({ analise_chat_ativo: ativo }, { onSuccess: () => setAtivo(undefined) });
  };

  if (!canUse) {
    return (
      <Card className="rounded-2xl border-border shadow-sm">
        <CardContent className="flex items-center gap-4 p-6">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted">
            <Lock className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">
              Análise Semanal de Chats
            </p>
            <p className="text-sm text-muted-foreground">
              Disponível nos planos <span className="font-medium text-foreground">Pro, Premium e PMK</span>.
              Faça upgrade para desbloquear insights automáticos e sugestões de melhoria de prompt.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (configLoading) {
    return (
      <Card className="rounded-2xl border-border shadow-sm">
        <CardContent className="flex items-center justify-center p-8">
          <ThinkingOrbLoader state="solving" size={20} aria-label="Carregando análise" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Configuração ──────────────────────────────────────────────────── */}
      <Card className="rounded-2xl border-border shadow-sm hover:shadow-md transition-shadow duration-200">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent">
              <BrainCircuit className="h-5 w-5 text-accent-foreground" />
            </div>
            <div>
              <CardTitle className="text-base font-semibold text-foreground">
                Análise Semanal de Chats
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground mt-0.5">
                A IA analisa padrões dos chats toda segunda às 03:00 BRT e sugere melhorias no prompt
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Cota mensal */}
          <div className="rounded-lg border bg-muted p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-muted-foreground">Análises este mês</p>
              <p className="text-xs font-semibold text-foreground">
                {isUnlimited ? (
                  <span className="text-accent-foreground">Ilimitado</span>
                ) : (
                  <span className={quotaExceeded ? 'text-danger-text' : 'text-foreground'}>
                    {runsThisMonth} de {analysisQuotaPerMonth}
                  </span>
                )}
              </p>
            </div>
            {!isUnlimited && (
              <Progress
                value={quotaPercent}
                className={`h-1.5 ${quotaExceeded ? '[&>div]:bg-destructive' : '[&>div]:bg-primary'}`}
              />
            )}
            {quotaExceeded && (
              <p className="text-xs text-danger-text">
                Cota mensal atingida. Renova no dia 1° do próximo mês.
              </p>
            )}
          </div>

          {/* Toggle análise automática */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-sm font-medium">Análise automática semanal</p>
              <p className="text-xs text-muted-foreground">
                Roda toda segunda às 03:00 BRT. Você pode disparar manualmente a qualquer momento.
              </p>
            </div>
            <Switch
              checked={analiseAtivo}
              disabled={readOnly}
              onCheckedChange={(v) => setAtivo(v)}
            />
          </div>

          {/* Última execução */}
          {config?.analise_ultima_execucao && (
            <p className="text-xs text-muted-foreground px-1">
              Última análise:{' '}
              {new Date(config.analise_ultima_execucao).toLocaleString('pt-BR', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </p>
          )}

          {/* Ações */}
          <div className="flex items-center gap-2 pt-1">
            {hasDraft && !readOnly && (
              <Button size="sm" onClick={handleSave} disabled={saveConfig.isPending}>
                {saveConfig.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                Salvar
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => triggerAnalysis.mutate()}
              disabled={triggerAnalysis.isPending || readOnly || quotaExceeded}
              title={quotaExceeded ? 'Cota mensal atingida' : undefined}
            >
              {triggerAnalysis.isPending ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              ) : (
                <Play className="mr-1 h-3 w-3" />
              )}
              Analisar agora
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Sugestões de prompt pendentes ─────────────────────────────────── */}
      {!!suggestions?.length && (
        <div className="space-y-2">
          {suggestions.map((s) => (
            <PromptSuggestionCard key={s.id} suggestion={s} clinicaId={clinicaId} readOnly={readOnly} />
          ))}
        </div>
      )}

      {/* ── Relatórios recentes ────────────────────────────────────────────── */}
      {reportsLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando relatórios...
        </div>
      ) : reports?.length ? (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Relatórios recentes
          </p>
          {reports.map((report) => (
            <Card key={report.id} className="rounded-xl border-border shadow-sm">
              <div
                className="flex cursor-pointer items-center justify-between px-4 py-3"
                onClick={() => setExpandedReport(expandedReport === report.id ? null : report.id)}
              >
                <div className="flex items-center gap-3">
                  <RefreshCw className="h-4 w-4 text-accent-foreground" />
                  <div>
                    <p className="text-sm font-medium">
                      {new Date(report.criado_em).toLocaleString('pt-BR', {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {report.total_conversas} conversas · ${Number(report.custo_estimado_usd).toFixed(4)} USD
                    </p>
                  </div>
                </div>
                {expandedReport === report.id ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </div>

              {expandedReport === report.id && (
                <CardContent className="space-y-4 pt-0">
                  {report.resultado.camadas.map((layer) => (
                    <div key={layer.periodo} className="space-y-2 rounded-lg border p-3">
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {PERIODO_LABELS[layer.periodo]}
                        </p>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${SENTIMENTO_COLORS[layer.sentimento_geral]}`}>
                          {layer.sentimento_geral}
                        </span>
                        <span className="ml-auto text-xs text-muted-foreground">
                          {layer.total_conversas} conversas
                        </span>
                      </div>

                      <p className="text-sm text-foreground">{layer.resumo_narrativo}</p>

                      {!!layer.perguntas_frequentes.length && (
                        <div>
                          <p className="mb-1 text-xs font-medium text-muted-foreground">Perguntas frequentes</p>
                          <div className="flex flex-wrap gap-1">
                            {layer.perguntas_frequentes.map((q, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">{q}</Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {!!layer.queixas_recorrentes.length && (
                        <div>
                          <p className="mb-1 text-xs font-medium text-danger-text">Queixas recorrentes</p>
                          <div className="flex flex-wrap gap-1">
                            {layer.queixas_recorrentes.map((q, i) => (
                              <Badge key={i} variant="outline" className="border-destructive/30 text-xs text-danger-text">{q}</Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {!!layer.temas_detectados.length && (
                        <div>
                          <p className="mb-1 text-xs font-medium text-muted-foreground">Temas</p>
                          <div className="flex flex-wrap gap-1">
                            {layer.temas_detectados.map((t, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{t}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  <p className="text-xs text-muted-foreground">
                    Modelos: {report.modelo_compressao} · {report.modelo_insights}
                  </p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      ) : null}
    </div>
  );
};
